/**
 * Header scroll state — reports, never styles.
 *
 * Sets on <html> (not <body>: Barba replaces the body's classes on navigation):
 *   data-header-scrolled   "true" once the page leaves the top, else "false"
 *   data-header-direction  "down" | "up" — the last direction scrolled by more
 *                          than --header-scroll-tolerance px (jitter ignored)
 *   data-header-past-hero  "true" once the header's bottom edge passes the
 *                          hero's (#hero), else "false" (also without a hero)
 *
 * What each state does (fixed header, hiding on scroll, turning solid)
 * is the template's CSS, from `$header` in sass/{template}/_config.scss.
 *
 * @param {import('lenis').default} lenisInstance The initialized Lenis instance.
 */
let currentLenisInstance = null;
let scrollHandler = null;
let resizeHandler = null;

export function initializeStickyHeader(lenisInstance) {
    if (!lenisInstance) return;

    if (scrollHandler) {
        destroyStickyHeader();
    }

    const header = document.getElementById('header');
    if (!header) return;

    const root = document.documentElement;
    const tolerance = parseFloat(getComputedStyle(root).getPropertyValue('--header-scroll-tolerance')) || 8;

    let lastScroll = lenisInstance.scroll;
    let anchor = lastScroll; // Scroll position where the current direction began.
    let direction = root.dataset.headerDirection || 'up';

    /**
     * @param {string} name  Data attribute name (camelCase dataset key).
     * @param {string} value New value; only written when it changes.
     */
    const set = (name, value) => {
        if (root.dataset[name] !== value) root.dataset[name] = value;
    };

    const update = (scroll) => {
        // Direction: flip only after `tolerance` px of travel the other way.
        if ((direction === 'down' && scroll < lastScroll) || (direction === 'up' && scroll > lastScroll)) {
            if (Math.abs(scroll - anchor) >= tolerance) {
                direction = scroll > lastScroll ? 'down' : 'up';
            }
        } else {
            anchor = scroll;
        }
        lastScroll = scroll;

        const hero = document.getElementById('hero');
        const pastHero = hero ? hero.getBoundingClientRect().bottom <= header.getBoundingClientRect().bottom : false;

        set('headerScrolled', scroll > 1 ? 'true' : 'false');
        set('headerDirection', scroll <= 1 ? 'up' : direction);
        set('headerPastHero', pastHero ? 'true' : 'false');
    };

    scrollHandler = (e) => update(e.scroll);
    resizeHandler = () => update(lenisInstance.scroll);

    update(lenisInstance.scroll);

    currentLenisInstance = lenisInstance;
    lenisInstance.on('scroll', scrollHandler);
    window.addEventListener('resize', resizeHandler);
}

export function destroyStickyHeader() {
    if (currentLenisInstance && scrollHandler) {
        currentLenisInstance.off('scroll', scrollHandler);
    }
    if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
    }
    scrollHandler = null;
    resizeHandler = null;
    currentLenisInstance = null;
}
